<template lang="pug">
  v-container.mt-0
    v-form(ref="form" v-model="valid")
      v-layout(wrap)
        v-flex(xs12 v-if="!emailListsFilter.length")
          div.d-flex.flex-row.ml-0
            v-icon person_add
            p.pa-2.mt-4.disabled We couldn’t find any static lists that have not been synced to a custom audience. To create an audience, add a new static list first. Click 
              a(href='https://lonewolf.my.site.com/s/article/LD-Create-a-static-list-LionDesk' target='_blank') here 
              | to learn more.
            v-text-field(:rules="emailListsFilterRules" v-show="false")
        v-flex(v-else)
            v-select(
              attach
              v-model="staticList"
              :items="emailListsFilter"
              :rules="emailListsFilterRules"
              placeholder="Static list"
              item-text="name"
              item-value="id"
              prepend-icon="person_add"
            )
            p.ml-4 We recommend including at least 300 contacts in your static list for improved targeting when using your custom audience.        
        v-flex(xs12 pb-0)
          v-radio-group(v-model="selectedFbAccountType")
            v-radio(
              :value="fbTypes['fbPersonal'].type"
            )
              template(slot="label")
                span.black--text Use my Facebook Business ad account
            p.ml-4.mt-3.mb-1(v-if="!isPersonalFbConnected") Create and target custom audience using <strong>your own ad account</strong> in Facebook Business. Connect your account to begin.
            p.ml-4.mt-3.mb-1(v-if="isPersonalFbConnected && fbPersonalAdAccounts.length") Create and target custom audience using <strong>your own ad account</strong> in Facebook Business.
        v-flex(xs12 pt-0 class="quit-space-top", v-if="fbPersonalAdAccounts.length")
          v-select(
            attach
            v-model="personalAdAccount"
            placeholder="Ad account"
            :items="personalAdAccountList"
            item-text="name"
            item-value="id"
            :rules="adAccountControlRules"
            class="ml-4"
            :disabled="selectedFbAccountType !== fbTypes['fbPersonal'].type"
          )
          .d-inline-block.mt-2.ml-4
            a.font-weight-bold.accent--text(@click="$emit('connect-to-facebook')")
              | Ad account settings
        v-flex(xs12, v-else)
          v-text-field(:rules="adAccountControlRules" v-show="false")
          div.quit-space-top.text-xs-center(v-if="!isPersonalFbConnected")
            v-btn.ml-0.primary(data-cy="connect-fb" :disabled="isFbLiondeskSelected" @click="$emit('connect-to-facebook')") Connect
          div.quit-space-top(v-else)
            p.ml-4.disabled We couldn’t find any ad accounts connected to LionDesk. Click on Facebook Settings to manage your ad account connections.
            div.text-xs-center
              v-btn.ml-0.primary(data-cy="create-ad-account-fb" @click="$emit('connect-to-facebook')")
                | FACEBOOK SETTINGS
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

import facebookTypes from '@/Contacts/config/facebook-source-types';

export default {
  data() {
    return {
      personalAdAccount: null,
      staticList: null,
      valid: false,
      fbTypes: facebookTypes,
      selectedFbAccountType: null,
    };
  },
  created() {
    this.getFbData(true);
    const { fbPersonal } = facebookTypes;
    this.selectedFbAccountType = fbPersonal.type;
  },
  computed: {
    ...mapState('facebookBusiness', ['fbPersonalAdAccounts', 'fbLiondeskAdAccount', 'fbAccountType']),
    ...mapState('contactsTable', ['selectedEmailListId', 'isShownCreateCustomAudienceFb']),

    ...mapGetters('contactsTable', ['emailLists']),
    ...mapGetters('facebookBusiness', ['isPersonalFbConnected', 'isFbConnected','hasAdsManagementPermission']),

    emailListsFilter() {
      return this.emailLists.filter(list => !list.fb_user_type_id);
    },
    adAccountControlRules() {
      if(!this.isFbLiondeskSelected) {
        return [v => !!v || 'Ad account is required'];
      }
      return [];
    },
    emailListsFilterRules() {
      return [v => !!v || 'Static List is required'];
    },
    isFbLiondeskSelected() {
      return this.selectedFbAccountType === facebookTypes.fbLiondesk.type;
    },
    personalAdAccountList() {
      return this.fbPersonalAdAccounts.map(ad => ({
            id: ad.fb_ad_id,
            name: `${ad.fb_ad_account_name} (${ad.fb_ad_account_id})`,
          })
      );
    },
  },
  watch: {
    valid: {
      handler(isValid) {
        this.$emit('valid-form', isValid);
      },
    },
    selectedEmailListId: {
      handler(staticListId) {
        if(staticListId) this.staticList = staticListId;
      }
    },
    isShownCreateCustomAudienceFb(isActive) {
      if(!isActive) {
        this.clearFields();
      }
    },
    selectedFbAccountType(isPersonal) {
      this.setFbSelectedType(isPersonal);
    },
  },

  methods: {
    ...mapActions('customAudiences', ['createCustomAudience']),
    ...mapActions('facebookBusiness', [
      'getFacebookPermissions',
      'getFbAdAccount',
      'transferInternalFbToUser',
    ]),
    ...mapMutations('customAudiences', ['setFbSelectedType']),
    ...mapMutations('facebookBusiness', ['setFbAccountType']),

    async saveCustomAudience() {
      let fbAdId = this.personalAdAccount;
      if(this.isFbLiondeskSelected) {
        if (!this.fbLiondeskAdAccount.length) {
          await this.transferInternalFbToUser();
          await this.getFbData();
        }
        fbAdId = this.fbLiondeskAdAccount.fb_ad_id;
      }

      if (this.$refs.form.validate()) {
        const payload = {
          static_list_id: this.staticList,
          fb_ad_id: fbAdId,
        };
        await this.createCustomAudience(payload);
        this.$emit('changes-saved');
      }
    },
    isValidForm() {
      return this.valid;
    },
    clearFields() {
      this.personalAdAccount = null;
      this.staticList = this.selectedEmailListId;
      this.$refs.form.resetValidation();
    },
    async getFbData(fbPersonal = false) {
      this.setFbAccountType(
        fbPersonal ? facebookTypes.fbPersonal.type : facebookTypes.fbLiondesk.type
      );
      const fbUser = {
        id: facebookTypes[this.fbAccountType].id,
        type: this.fbAccountType,
      };
      await this.getFacebookPermissions(fbUser);
      if (this.isFbConnected && this.hasAdsManagementPermission) {
        await this.getFbAdAccount();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
  p {
    font-size:14px
  }

  p.disabled {
    opacity: .6;
  }

  .quit-space-top {
    margin-top: -15px;
  }
</style>
